import moment from 'moment-timezone';
import { normalizeWaiverText, type ActivationWaiverSet } from '@/lib/waivers';

export interface ShowRequestPayload {
  hashed_show_id: string;
  specialist_id: number;
  timestamp: string;
  qrTime: string;
}

const globalShowIDs = [23, 15, 56, 43];

export interface ShowData {
  show_id: number;
  show_type: string;
  team_id: number;
  season_id: number;
  master_show_id: number;
  canadian_show: number;
  show_name: string;
  start_date: string;
  end_date: string;
  waiver: string | null;
  minor_waiver: string | null;
  status: string;
  n: string;
}

interface ShowType {
  rideDrive: boolean;
  simulator: boolean;
  greenscreen: boolean;
  posterMaker: boolean;
}

const GENERAL_WAIVER_LOCALES = ['en', 'es', 'fr'] as const;

/**
 * `shows.waiver` / `shows.minor_waiver` arrive in one of three shapes (SA-1671):
 *  - per-activation only: every top-level key is an activation type (`broncoMountain`, `rideDrive`, …).
 *  - hybrid: top-level `en`/`es`/`fr` general copy ALONGSIDE some activation-type keys.
 *  - legacy: only `en`/`es`/`fr` — one waiver for every activation, no per-activation keys at all.
 * A key only counts as general locale text when its value is itself a string; anything else
 * (including a key literally named `en`/`es`/`fr` whose value is an object) is treated as a
 * per-activation entry instead, since only a string at that position can BE locale text.
 */
function parseWaiverJSON(raw: string | null): {
  general: WaiverTextMap | null;
  perActivation: Record<string, LocalizedWaiverText>;
} {
  const empty = { general: null, perActivation: {} };
  if (!raw) return empty;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return empty;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty;

  const obj = parsed as Record<string, unknown>;
  const general: WaiverTextMap = {};
  for (const locale of GENERAL_WAIVER_LOCALES) {
    const value = obj[locale];
    if (typeof value === 'string' && value.trim().length > 0) general[locale] = value;
  }

  const perActivation: Record<string, LocalizedWaiverText> = {};
  for (const [key, value] of Object.entries(obj)) {
    if ((GENERAL_WAIVER_LOCALES as readonly string[]).includes(key)) continue;
    if (typeof value === 'string' || (value && typeof value === 'object' && !Array.isArray(value))) {
      perActivation[key] = value as LocalizedWaiverText;
    }
  }

  return { general: Object.keys(general).length > 0 ? general : null, perActivation };
}

/** A locale-keyed waiver text map (`{ en, es, fr }` — any subset). */
type WaiverTextMap = Record<string, string>;
/** A single activation's raw waiver copy: a plain string or a locale-keyed map. */
type LocalizedWaiverText = string | WaiverTextMap;

/**
 * Readability is decided in ONE place — `normalizeWaiverText` in src/lib/waivers.ts, the same
 * function `localizedWaiverMarkdown`/`injectWaivers` render from — so this file, the injector
 * and the page's signature gates can never disagree about whether a side exists (SA-1671).
 */

export class AutoShow {
  showID: number;
  showType: ShowType;
  teamID: number;
  seasonID: number;
  masterShowID: number;
  canadianShow: number;
  showName: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  status: string;
  n: string;

  private adultGeneral: WaiverTextMap | null;
  private adultPerActivation: Record<string, LocalizedWaiverText>;
  private minorGeneral: WaiverTextMap | null;
  private minorPerActivation: Record<string, LocalizedWaiverText>;
  /**
   * Every activation key `shows.show_type` flags as `1`, read generically (the way the
   * reference app's `waiverContent.tsx` reads it) rather than through the narrow `ShowType`
   * shape below, which only tracks four of the platform's activation types and would silently
   * exclude `broncoMountain`/`evRide` waivers a show genuinely offers.
   */
  private offeredActivations: Set<string>;

  constructor(data: ShowData) {
    this.showID = data.show_id;
    const rawShowType = data.show_type ? JSON.parse(data.show_type) : {};
    this.showType = {
      rideDrive: rawShowType?.rideDrive === 1,
      simulator: rawShowType?.simulator === 1,
      greenscreen: rawShowType?.greenscreen === 1,
      posterMaker: rawShowType?.posterMaker === 1,
    };
    this.offeredActivations = new Set(
      Object.entries(rawShowType ?? {})
        .filter(([, value]) => value === 1)
        .map(([key]) => key),
    );
    this.teamID = data.team_id;
    this.seasonID = data.season_id;
    this.masterShowID = data.master_show_id;
    this.canadianShow = data.canadian_show;
    this.showName = data.show_name;
    this.startDate = moment(data.start_date).startOf('day');
    this.endDate = moment(data.end_date).endOf('day');

    const adultParsed = parseWaiverJSON(data.waiver);
    this.adultGeneral = adultParsed.general;
    this.adultPerActivation = adultParsed.perActivation;

    const minorParsed = parseWaiverJSON(data.minor_waiver);
    this.minorGeneral = minorParsed.general;
    this.minorPerActivation = minorParsed.perActivation;

    this.status = data.status;
    this.n = data.n;
  }

  /**
   * Builds the per-activation waiver map for `injectWaivers` (src/lib/waivers.ts), for the
   * given `?t=` source. Candidate keys are every activation either side authored a specific
   * entry for, but only those the show's `show_type` actually flags on ever make it into the
   * result — a show authoring copy for an activation it doesn't run must not put that waiver in
   * front of a visitor doing something else. The visitor's own activation is always included
   * regardless of `show_type` (so a show with no per-activation keys at all, or an imperfectly
   * flagged `show_type`, still shows its one waiver rather than none — the original SA-1671 bug).
   * Within an included key, a side (adult or minor) with no specific entry of its own falls back
   * to that side's general/legacy text when the show has any — this also covers the mixed case
   * where one side is per-activation and the other is legacy-only. A key with no actual readable
   * text on either side (see `normalizeWaiverText`) is omitted entirely.
   */
  getWaiverSet(source: string): ActivationWaiverSet {
    const sourceKey = this.mapSourceToWaiverKey(source);
    const candidateKeys = new Set<string>([
      ...Object.keys(this.adultPerActivation),
      ...Object.keys(this.minorPerActivation),
    ]);

    const keys = new Set<string>([sourceKey]);
    for (const key of candidateKeys) {
      if (this.offeredActivations.has(key)) keys.add(key);
    }

    const result: ActivationWaiverSet = {};
    for (const key of keys) {
      // Normalize BEFORE the general fallback: a per-activation entry that carries no readable
      // text (`{ en: '' }`, `{ en: 42 }`, `''`) is absent, not present-and-empty, so the show's
      // general copy fills in for it exactly as a missing key would. Each side is stored already
      // narrowed to its readable locales, or null — never a shape a later truthiness check could
      // mistake for content (SA-1671).
      const adult = normalizeWaiverText(this.adultPerActivation[key]) ?? normalizeWaiverText(this.adultGeneral);
      const minor = normalizeWaiverText(this.minorPerActivation[key]) ?? normalizeWaiverText(this.minorGeneral);
      if (adult !== null || minor !== null) result[key] = { adult, minor };
    }
    return result;
  }

  /**
   * Maps the source parameter to the corresponding waiver key
   * @param source The source parameter from the URL (e.g., 'broncoMountainRegistration')
   * @returns The corresponding waiver key (e.g., 'broncoMountain')
   */
  private mapSourceToWaiverKey(source: string): string {
    const mapping: Record<string, string> = {
      broncoMountainRegistration: 'broncoMountain',
      rideDriveRegistration: 'rideDrive',
      simulatorRegistration: 'simulator',
      evRideRegistration: 'evRide',
    };

    return mapping[source] || source;
  }

  get isLincoln(): boolean {
    return this.teamID === 1;
  }

  get isFord(): boolean {
    return this.teamID === 5;
  }

  get isCanadian(): boolean {
    return this.canadianShow === 1;
  }

  get isGlobalShow(): boolean {
    return globalShowIDs.includes(this.masterShowID);
  }

  get isActive(): boolean {
    const now = moment();
    return this.status === 'active' && now.isSameOrAfter(this.startDate) && now.isSameOrBefore(this.endDate);
  }

  get hasRideDrive(): boolean {
    return this.showType.rideDrive;
  }

  get hasSimulator(): boolean {
    return this.showType.simulator;
  }

  get hasGreenscreen(): boolean {
    return this.showType.greenscreen;
  }

  get hasPosterMaker(): boolean {
    return this.showType.posterMaker;
  }
}
